import type { PassViewRecord } from './fetchWeldViewApis';

export interface TlogInputRow {
  seq?: number;
  travel_speed?: number | null;
  current?: number | null;
  volts?: number | null;
  wire_speed?: number | null;
  oscillation_width?: number | null;
  true_energy?: number | null;
  heat?: number | null;
  tilt?: number | null;
  distance?: number | null;
  ignore_record?: number | null;
  incomplete_flag?: number | null;
}

export interface PassScoringRequestBody {
  weld_number: string;
  job_number: string;
  pass_name: string;
  welding_system: string;
  welduid_s_event_torch: string;
  s_time: number;
  c_time: number;
  current_avg?: number | null;
  current_high?: number | null;
  current_low?: number | null;
  volts_avg?: number | null;
  volts_high?: number | null;
  volts_low?: number | null;
  travel_speed_avg?: number | null;
  travel_speed_high?: number | null;
  travel_speed_low?: number | null;
  wire_speed_avg?: number | null;
  wire_speed_high?: number | null;
  wire_speed_low?: number | null;
  heat_avg?: number | null;
  is_last_pass?: boolean;
  tlog?: TlogInputRow[];
  welder_id?: string | null;
}

function num(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function parseEpochMs(raw: string | undefined): number {
  if (!raw) return Date.now();
  const n = Number(raw);
  if (!Number.isFinite(n)) return Date.now();
  return n <= 1e12 ? n * 1000 : n;
}

/**
 * Build a WeldIQ PassScoringRequest body from Data360 pass-view + optional T-log rows.
 */
export function buildPassScoringRequest(
  weldNumber: string,
  jobNumber: string,
  pass: PassViewRecord,
  options: {
    weldingSystem?: string;
    welduid?: string;
    tlogRows?: TlogInputRow[];
    isLastPass?: boolean;
    rawPass?: Record<string, unknown>;
  } = {},
): PassScoringRequestBody {
  const raw = options.rawPass ?? {};
  const params = pass.params ?? {};
  const sTime = parseEpochMs(pass.startTime);
  const cTime = parseEpochMs(pass.endTime);
  const cFinal = cTime >= sTime ? cTime : sTime + 1;

  return {
    weld_number: weldNumber,
    job_number: jobNumber,
    pass_name: pass.name,
    welding_system: options.weldingSystem || String(raw.welding_system ?? 'unknown'),
    welduid_s_event_torch:
      options.welduid ||
      String(raw.welduid_s_event_torch ?? raw.id ?? `${weldNumber}_${pass.name}`),
    s_time: sTime,
    c_time: cFinal,
    current_avg: num(params.current?.avg ?? raw.current_avg),
    current_high: num(params.current?.max ?? raw.current_high),
    current_low: num(params.current?.min ?? raw.current_low),
    volts_avg: num(params.volt?.avg ?? raw.volts_avg),
    volts_high: num(params.volt?.max ?? raw.volts_high),
    volts_low: num(params.volt?.min ?? raw.volts_low),
    travel_speed_avg: num(params.travel?.avg ?? raw.travel_speed_avg),
    travel_speed_high: num(params.travel?.max ?? raw.travel_speed_high),
    travel_speed_low: num(params.travel?.min ?? raw.travel_speed_low),
    wire_speed_avg: num(params.wire?.avg ?? raw.wire_speed_avg),
    wire_speed_high: num(params.wire?.max ?? raw.wire_speed_high),
    wire_speed_low: num(params.wire?.min ?? raw.wire_speed_low),
    heat_avg: num(params.heat?.avg ?? raw.heat_avg),
    welder_id: pass.welderId || null,
    is_last_pass: options.isLastPass ?? false,
    tlog: options.tlogRows ?? [],
  };
}

export function tlogFromDetailRecords(
  records: { time?: number | null; volts?: number | null; current?: number | null; travelSpeed?: number | null; heat?: number | null }[],
): TlogInputRow[] {
  return records.map((r, i) => ({
    seq: i + 1,
    travel_speed: r.travelSpeed,
    current: r.current,
    volts: r.volts,
    heat: r.heat,
  }));
}

export interface IndicationIdentityPayload {
  weld_number: string;
  job_number: string;
  pass_name?: string;
}

/**
 * Build indication-check payload. Uses DB fetch (pass_name only) unless tlog rows are provided.
 */
export function buildIndicationRequest(
  weldNumber: string,
  jobNumber: string,
  options: {
    pass?: PassViewRecord;
    passName?: string;
    weldingSystem?: string;
    tlogRows?: TlogInputRow[];
    isLastPass?: boolean;
  } = {},
): IndicationIdentityPayload | PassScoringRequestBody {
  const passName = options.passName ?? options.pass?.name;
  const tlogRows = options.tlogRows ?? [];

  if (passName && tlogRows.length > 0 && options.pass) {
    return buildPassScoringRequest(weldNumber, jobNumber, options.pass, {
      weldingSystem: options.weldingSystem,
      tlogRows,
      isLastPass: options.isLastPass,
    });
  }

  if (passName) {
    return {
      weld_number: weldNumber,
      job_number: jobNumber,
      pass_name: passName,
    };
  }

  return {
    weld_number: weldNumber,
    job_number: jobNumber,
  };
}
